import { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import { ApiResponse } from "apisauce";
import { toast } from "react-toastify";

import { RoyaltyReportResponse } from "../../../interface/api/RoyaltyReportResponse";
import { RoyaltyReportPayload } from "../../../interface/api/RoyaltyReportPayload";
import { RoyaltyConfig } from "../../../interface/api/RoyaltyConfig";
import { RoyaltyReportPanelProps } from "../../../interface/forms/RoyaltyReportPanelProps";
import royaltyService from "../../../services/royaltyService";
import { failedReportResult } from "../../../utils/report-run";
import FormDatePicker from "../../shared/form-date-picker";
import ReportRunActions from "../report-run-actions";
import { useAppSelector, useReportRun } from "../../../hooks";
import { formatDate } from "../../../utils/date-helper";
import { formatCurrency } from "../../../utils/parser";
import { getCurrentUser } from "../../../store/userStore";
import {
  NetPriceStreams,
  RoyaltyStreamKey,
  RoyaltyStreamOptions,
  StreamKey,
  royaltyStreamLabel,
} from "../../../utils/constants";
import { buildRoyaltySummary } from "./royalty-report-summary";
import RoyaltyReportLines from "./royalty-report-lines";

const RoyaltyReportPanel = ({ onRunStateChange }: RoyaltyReportPanelProps) => {
  const user = useAppSelector(getCurrentUser);
  const run = useReportRun(onRunStateChange);

  const [config, setConfig] = useState<RoyaltyConfig | null>(null);
  const [stream, setStream] = useState<RoyaltyStreamKey>("main");
  const [partnerKey, setPartnerKey] = useState("");
  const [currentCPI, setCurrentCPI] = useState("");
  const [townRate, setTownRate] = useState("");
  // Pre-filled from Settings; changing it here overrides it for this run only.
  const [netTicketPrice, setNetTicketPrice] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [includeMissingSalePrice, setIncludeMissingSalePrice] = useState(false);
  const [estimateMissingSalePrice, setEstimateMissingSalePrice] =
    useState(false);
  const [report, setReport] = useState<RoyaltyReportResponse | null>(null);
  // Kept so the exported workbook can name the run it came from.
  const [lastRun, setLastRun] = useState<RoyaltyReportPayload | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      const response: ApiResponse<any> = await royaltyService.getConfig();
      if (response.ok && response.data) {
        setConfig(response.data);
        setNetTicketPrice(String(response.data.netTicketPrice ?? ""));
        if (response.data.partners.length > 0) {
          setPartnerKey(response.data.partners[0].key);
        }
      } else {
        toast.error("Failed to load royalty configuration");
      }
    };
    loadConfig();
  }, []);

  const cpiNum = parseFloat(currentCPI);
  const townNum = parseFloat(townRate);
  const netPriceNum = parseFloat(netTicketPrice);

  const usesNetPrice =
    stream === "all" || NetPriceStreams.includes(stream as StreamKey);

  const partnerLabel = (p: { name: string; rate: number }) =>
    `${p.name} (${(p.rate * 100).toFixed(0)}%)`;

  const onGenerate = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a date range");
      return;
    }
    if (Number.isNaN(cpiNum) || Number.isNaN(townNum)) {
      toast.error("Please enter the current CPI and town royalty rate");
      return;
    }
    if (usesNetPrice && Number.isNaN(netPriceNum)) {
      toast.error("Please enter the net ticket price");
      return;
    }

    const payload: RoyaltyReportPayload = {
      fromDate,
      toDate,
      stream,
      partner: partnerKey,
      currentCPI: cpiNum,
      townRate: townNum,
      email: user?.username ?? "",
      netTicketPrice: usesNetPrice ? netPriceNum : null,
      includeMissingSalePrice,
      estimateMissingSalePrice,
    };

    setReport(null);
    setLastRun(payload);
    run.start("Pricing tickets and building the workbook...");

    try {
      const response: ApiResponse<any> =
        await royaltyService.generateReport(payload);

      if (response.ok && response.data) {
        setReport(response.data);
        run.publish(buildRoyaltySummary(response.data, payload));
      } else {
        run.publish({
          status: "error",
          headline:
            typeof response.data === "string"
              ? response.data
              : "The royalty report could not be generated.",
          note: `The server replied ${response.status ?? "with no status"}.`,
        });
      }
    } catch (error) {
      run.publish(failedReportResult(error));
    }
  };

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Stream</InputLabel>
            <Select
              value={stream}
              onChange={(e) => setStream(e.target.value as RoyaltyStreamKey)}
              label="Stream"
            >
              {RoyaltyStreamOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Partner</InputLabel>
            <Select
              value={partnerKey}
              onChange={(e) => setPartnerKey(e.target.value as string)}
              label="Partner"
            >
              {(config?.partners ?? []).map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {partnerLabel(p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Current CPI"
            type="number"
            value={currentCPI}
            onChange={(e) => setCurrentCPI(e.target.value)}
            variant="outlined"
            inputProps={{ step: "0.1" }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Current town royalty rate ($)"
            type="number"
            value={townRate}
            onChange={(e) => setTownRate(e.target.value)}
            variant="outlined"
            inputProps={{ step: "0.01" }}
          />
        </Grid>
        {usesNetPrice && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Net ticket price, Hydrovac / Small Quantities ($)"
              type="number"
              value={netTicketPrice}
              onChange={(e) => setNetTicketPrice(e.target.value)}
              variant="outlined"
              inputProps={{ step: "0.01" }}
              helperText={
                config && netPriceNum !== config.netTicketPrice
                  ? `Overrides the ${formatCurrency(config.netTicketPrice)} in Settings for this run`
                  : "From Settings. Used instead of the sale price for these streams."
              }
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <FormDatePicker
            label="From date"
            value={fromDate}
            onChange={(date: any) => setFromDate(date)}
            clearable
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormDatePicker
            label="To date"
            value={toDate}
            onChange={(date: any) => setToDate(date)}
            clearable
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeMissingSalePrice || estimateMissingSalePrice}
                onChange={(e) => setIncludeMissingSalePrice(e.target.checked)}
                // Estimating a price is also a way of listing the ticket, so the
                // include box cannot be turned off underneath it.
                disabled={estimateMissingSalePrice}
                color="primary"
              />
            }
            label="Include tickets with no selling price (treated as $0.00)"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={estimateMissingSalePrice}
                onChange={(e) =>
                  setEstimateMissingSalePrice(e.target.checked)
                }
                color="primary"
              />
            }
            label="Estimate missing selling prices from the period average"
          />
          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            style={{ marginLeft: 32 }}
          >
            Tickets with no selling price are valued at the average of the priced
            tickets in the same period, so the month closes with nothing left
            unpriced. The estimated royalty is reported and totalled separately.
          </Typography>
        </Grid>
      </Grid>

      <Box style={{ marginTop: 16 }}>
        <ReportRunActions
          title="Royalty Report"
          run={run}
          onRun={onGenerate}
          label="Generate report"
          runningLabel="Generating..."
          disabled={!config}
          maxWidth="md"
        >
          {report && (
            <RoyaltyReportLines
              lines={report.lines}
              grouped={report.streams.length > 1}
              skipped={report.unusedTickets}
              unlistedUnpricedCount={
                report.unpricedTicketCount - report.missingSalePriceCount
              }
              fileName={
                lastRun
                  ? `Royalty ${report.partnerName} ${royaltyStreamLabel(lastRun.stream)} ` +
                    `${formatDate(lastRun.fromDate, false)} to ` +
                    `${formatDate(lastRun.toDate, false)}`.replace(/\//g, "-")
                  : undefined
              }
            />
          )}
        </ReportRunActions>
      </Box>
    </div>
  );
};

export default RoyaltyReportPanel;
